import { MutationHandlingServiceV2 } from '@akebono/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { of, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { AutoLotView, validateAllFormFields } from 'src/app/const';
import {
  LotSource,
  LotStatusEnum,
  LotTypeEnum,
  LotUpdateGQL,
} from 'src/app/modules/graphql/service/graphql-auto-main-service';

@Component({
  selector: 'app-lot-info-edit-modal',
  templateUrl: './lot-info-edit-modal.component.html',
  styleUrls: ['./lot-info-edit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotInfoEditModalComponent implements OnInit, OnDestroy {
  @Input() lot: AutoLotView;
  @Input() lotType: LotTypeEnum;
  @Input() lotSource: LotSource;
  @Input() canUpdateLotInfo: boolean;
  @Input() canEditEngineVolume: boolean;
  @Output() actionComplete = new EventEmitter<void>();

  form: FormGroup;

  saving$ = of(false);

  private destroy$ = new Subject<void>();

  fromGrid = {
    labelSpan: 9,
    controlSpan: 24 - 9,
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly modal: NzModalRef,
    private readonly mhs: MutationHandlingServiceV2,
    private readonly lotUpdateGQL: LotUpdateGQL,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      company: this.fb.control({
        value: this.lot.company,
        disabled: !((this.isAuctionEnd || this.isManualLot) && this.canUpdateLotInfo),
      }),
      model: this.fb.control({
        value: this.lot.modelName,
        disabled: !((this.isAuctionEnd || this.isManualLot) && this.canUpdateLotInfo),
      }),
      rating: this.fb.control({
        value: this.lot.rate,
        disabled: !((this.isAuctionEnd || this.isManualLot) && this.canUpdateLotInfo),
      }),
      year: this.fb.control({
        value: Number(this.lot.year) || null,
        disabled: !((this.isAuctionEnd || this.isManualLot) && this.canUpdateLotInfo),
      }),
      engineVolume: this.fb.control({
        value: this.lot.engineVolume,
        disabled: !((this.isAuctionEnd || this.isManualLot) && this.canEditEngineVolume),
      }),
      color: this.fb.control({
        value: this.lot.color,
        disabled: !(this.isAuctionEnd && this.canUpdateLotInfo),
      }),
      frame: this.fb.control({
        value: this.lot.frame,
        disabled: !(this.isAuctionEnd && this.canUpdateLotInfo),
      }),
      mileage: this.fb.control({
        value: Number(this.lot.mileage) || null,
        disabled: !(this.isAuctionEnd && this.canUpdateLotInfo),
      }),
      grade: this.fb.control({
        value: this.lot.modelGrade,
        disabled: !(this.isAuctionEnd && this.canUpdateLotInfo),
      }),
      transmission: this.fb.control({
        value: this.lot.transmission,
        disabled: !(this.isAuctionEnd && this.canUpdateLotInfo),
      }),
    });

    console.log(this.isAuctionEnd, this.lot.status);
  }

  submit(): void {
    validateAllFormFields(this.form);

    if (this.form.invalid) {
      return;
    }

    const value = this.form.value;
    const ref = this.mhs.mutate(this.lotUpdateGQL, {
      input: {
        lotId: this.lot.id,
        lotType: this.lotType,
        lotSource: this.lotSource,
        companyEn: value.company,
        modelEn: value.model,
        colorEn: value.color,
        ratingEn: value.rating,
        modelTypeEn: value.frame,
        mileageNum: value.mileage,
        modelGradeEn: value.grade,
        year: value.year,
        engineVolumeNum: value.engineVolume,
        transmissionEn: value.transmission,
      },
    });

    this.saving$ = ref.loading;
    ref.data.pipe(first(), takeUntil(this.destroy$)).subscribe(() => {
      this.actionComplete.emit();
      this.close();
    });
  }

  close(): void {
    this.modal.destroy();
  }

  private get isAuctionEnd(): boolean {
    return (
      [LotStatusEnum.Won, LotStatusEnum.NegotiationSold, LotStatusEnum.Sold].includes(
        this.lot.status as LotStatusEnum,
      ) || false
    );
  }

  private get isManualLot(): boolean {
    return this.lotSource === LotSource.Manual;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
